import { h, defineComponent, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { create } from '@/services/api/resources/entityFolder';
import { remove as removeLabel, filter as fetchAllLabels, filterEntityFolderLabelsToOptions } from '@/services/api/resources/entityFolderLabel';
import { filter as fetchAllEntityItems } from '@/services/api/resources/entityItem';
import { filterListToOptions, sequence, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions, SelectOptionsWithId } from '@/ui/types';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useEntityFolderRecord } from '@/ui/pages/entityFolder/useEntityFolderRecord';
import { useEntityFolderForm } from '@/ui/pages/entityFolder/useEntityFolderForm';
import { queryTabName, EntityItemsTabNames } from '@/ui/components/navigation/constants';
import { fetchSelectOptions } from './EntityFolderChildrenTransforms';
import { filter as getAllUsers } from '@/services/api/resources/user';

import { RemoveLabelConfirmationModal } from '@/ui/components/entityFolderLabel/RemoveLabelConfirmationModal';
import { pipe } from 'fp-ts/lib/pipeable';

export const EntityFolderCreatePage = defineComponent({
  name: 'EntityFolderCreatePage',
  setup() {
    if (!hasFeature('entityFolders')) {
      return renderResourceTeaserPage(t('common.entity-folders'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_FOLDERS)) {
      return renderForbiddenPage;
    }

    const isRemovingLabel = ref<boolean>(false);
    const isRemoveLabelModalOpen = ref<boolean>(false);
    const labelToBeRemoved = ref<number>(0);
    const entityItemOptions = ref<SelectOptions>([]);
    const childrenOptions = ref<SelectOptions>([]);
    const userOptions = ref<SelectOptions>([]);
    const labelOptions = ref<SelectOptionsWithId<string>>([]);

    const record = useEntityFolderRecord();

    const getData = useTask(
      () => sequence(getAllUsers(), fetchAllEntityItems(), fetchAllLabels()),
      ([users, entityItems, labels]) => {
        userOptions.value = filterListToOptions(users);
        entityItemOptions.value = filterListToOptions(entityItems);
        labelOptions.value = filterEntityFolderLabelsToOptions(labels);

        fetchSelectOptions(null, childrenOptions, record).run();
      },
    );

    const openRemoveModalTask = (label: string) => pipe(() => {
      isRemoveLabelModalOpen.value = true;
      labelToBeRemoved.value = labelOptions.value.find(row => row.label === label)?.id || 0;
    });
    const removeLabelTask = useTask(
      () => removeLabel(labelToBeRemoved.value),
      () => {
        isRemoveLabelModalOpen.value = false;
        labelToBeRemoved.value = 0;

        getData.run();
      },
    );

    // send record
    const sendRecord = useTask(
      () => create(record.value),
      () => toastAndRedirect(t('entity-folder.created'), 'entityItems', { [queryTabName]: EntityItemsTabNames.FOLDERS }),
    );

    const { formFields, handleSubmit } = useEntityFolderForm(record, sendRecord, openRemoveModalTask);

    return function render() {
      return container(
        [
          h(RemoveLabelConfirmationModal, {
            props: { active: isRemoveLabelModalOpen.value, loading: isRemovingLabel.value },
            on: {
              input: () => {
                isRemoveLabelModalOpen.value = false;
              },
              'click:removeLabel': () => removeLabelTask.run(),
            },
          }),
          pageTitle(t('entity-folder.create')),
          box([
            loader('form', getData, [
              ...formFields(
                entityItemOptions.value, childrenOptions.value, userOptions.value, labelOptions.value,
                fetchSelectOptions.bind(fetchSelectOptions, null, childrenOptions),
              ),
              loadingButton(
                t('entity-folder.create'),
                handleSubmit,
                sendRecord.state.value.pending,
              ),
            ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
